import { QUERY_KEYS } from '@/constants/query';
import { useDappChainConfig, useProvider } from '@/hooks/web3/useChain';
import { BaseResponse, EnableAgentReqDto, EnableAgentRespDto, JwtTokenRespDto, UserLoginReqDto } from '@/types/agent';
import { getSynContract } from '@/utils/contract';
import { saveAgentJWTToken } from '@/utils/storage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { zeroAddress } from 'viem';
import { enableOrDisableGelatoAgent, gelatoLogin } from './api';
import { useAgentStore } from './store';

/**
 * login mutation
 */
export const useLoginMutation = () => {
  const { setJWTToken, setAgentAddress } = useAgentStore();
  return useMutation<AxiosResponse<BaseResponse<JwtTokenRespDto>>, Error, UserLoginReqDto>({
    mutationFn: (data: UserLoginReqDto) => {
      return gelatoLogin(data);
    },
    onSuccess: (data, variables) => {
      if (data?.data?.data?.token) {
        saveAgentJWTToken(variables.userAddr, data.data.data.token);
        setJWTToken(variables.userAddr, data.data.data.token);
        setAgentAddress(variables.userAddr, variables.userAddr);
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

/**
 * enable or disable agent mutation
 */
export const useEnableOrDisableAgentMutation = (userAddr: string) => {
  const { userAgentSettings } = useAgentStore();
  const jwtToken = userAgentSettings[userAddr]?.jwtToken;
  return useMutation<AxiosResponse<BaseResponse<EnableAgentRespDto>>, Error, EnableAgentReqDto>({
    mutationFn: (data: EnableAgentReqDto) => enableOrDisableGelatoAgent(data, jwtToken?.token),
    onSuccess: (data) => {
      if (data?.data?.data?.taskId) {
        console.log('Agent operation task created:', data.data.data.taskId);
      }
    },
    onError: (error) => {
      console.error('Enable/Disable agent failed:', error);
    },
  });
};

/**
 * get agent address query
 */
export const useFetchAgentAddressFromChainQuery = (chainId: number | undefined, userAddr: string | undefined) => {
  const dappConfig = useDappChainConfig(chainId);
  const provider = useProvider();
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.AGENT.AGENT_ADDRESS_FROM_CHAIN(chainId, userAddr),
    queryFn: async (): Promise<string | null | false> => {
      if (!userAddr || !chainId || !dappConfig?.contracts?.router || !provider) return null;
      const routerAddress = dappConfig?.contracts?.router;
      if (!routerAddress) return null;
      const routerContract = getSynContract({
        chainId,
        type: 'router',
        address: routerAddress,
        signerOrProvider: provider,
      });
      if (!routerContract) return null;
      const subAccount: string = await routerContract.callStatic.getSubAccount(userAddr);
      if (subAccount === zeroAddress) return false;

      useAgentStore.getState().setAgentAddress(userAddr, subAccount);

      return subAccount;
    },
    enabled: Boolean(userAddr && chainId && dappConfig?.contracts?.router && provider),
  });
};
